import React, { useCallback, useEffect } from 'react';
// Utils
import { delay, useSetState, getRoleName, validArray } from 'utils';
import { setActionFill, setBadgeType, mutateCloseEvent } from 'utils/events';
// App Context
import { useAppDispatch } from 'context/AppContext';
// Event Context
import { useEventDispatch, useEventState } from 'context/EventContext';
import { patchEvents, closeEvents } from 'context/EventContext/middleware';
import * as eventFlags from 'context/EventContext/statusFlags';
// Services
import Badge from 'elements/Badge';
import Button from 'elements/Button';
// Auth Context
import { useAuthState } from 'context/AuthContext';
import { OrderLinesProps, ProductionEventProps } from 'types/productionEvent';
// Elements
import Icon from 'assets/Icon';
import ListCell from 'elements/ListCell';
import { TextField } from 'elements/Forms';
import { Modal, ModalContent, ModalFooter, ModalHeader } from 'elements/Modal';
// Styles
import styles from './style/eventDetails.module.css';

interface ChildLineProps {
  event: ProductionEventProps
  childEvent: OrderLinesProps,
  actionActive: boolean,
  toggleAction: Function,
}

function SubEvent({
  event,
  childEvent,
  actionActive,
  toggleAction,
}:ChildLineProps) {
  // Auth State
  const { defaultOrganisation: { orgRef, roles } } = useAuthState();
  const { employee: { firstName, lastName, userId } } = useAuthState();
  // App State
  const appDispatch = useAppDispatch();
  // Event State
  const { eventFilters, eventState } = useEventState()
  const eventDispatch = useEventDispatch();

  // User Roles
  const userRoles = validArray(roles) ? roles.map(getRoleName) : [];
  const initialState = {
    event: {},
    loading: true,
    modalActive: false,
    confirmModalActive: false,
    comment: '',
    resendEvents: {},
  }
  const [state, setState] = useSetState(initialState);
  // Handlers
  async function patchData() {
    await patchEvents({
      appDispatch,
      eventDispatch,
      event: {
        targetSalesOrder: event.orderInfo.targetSalesOrder,
        targetItemNumber: childEvent.itemId ? childEvent.itemId.toString() : '',
        asn: '0',
        targetLineStatus: childEvent.processingStatus,
        targetShippedQuantity: childEvent.orderedQuantity ? childEvent.orderedQuantity.toString() : '',
        processingStatus: 'PROCESSED',  
        salesOrderId: childEvent.salesOrderId,
        salesOrderLineId: childEvent.salesOrderLineId,
        closureMessage: state.comment,
        fullName: `${firstName} ${lastName}`,
        userId: Number(userId),
      },
      eventFilters,
    })
    await closeEvents({
      appDispatch,
      eventDispatch,
      eventFilters,
      event: mutateCloseEvent(event, childEvent, state.comment, firstName, lastName, Number(userId)),
      orgRef,
    })
  }

  const initiatePage = useCallback(async () => {
    await delay(200);
    if (eventState === eventFlags.GET_EVENTS_SUCCESS || eventState === eventFlags.RESEND_EVENTS_SUCCESS) {
      setState({ loading: false });
    }
  }, [setState, eventState]);

  useEffect(() => {
    initiatePage();
  }, [initiatePage]);

  return (
    <div className={styles.container}>
      <>
        <div className={styles.grid}>
          <div className={styles.processingStatus}>
            <div className="subtitle1">
              <Badge
                badgeStyle={setBadgeType((childEvent.processingStatus === 'ERROR' || childEvent.processingStatus === 'LINES_IN_ERROR') ? 'FAILED' : childEvent.processingStatus)}
                label={(childEvent.processingStatus === 'ERROR' || childEvent.processingStatus === 'LINES_IN_ERROR') ? 'FAILED' : childEvent.processingStatus}
              />        
            </div>
          </div>
          <div className={styles.maindetail}>
            <span className="small-print">SO Line Id</span>
            <strong>
              {childEvent.salesOrderLineId || '-'}
            </strong>
          </div>
          <div className={styles.actions}>
            {(userRoles.includes('PLANNING') || userRoles.includes('PLANNING ADMIN') || userRoles.includes('WAREHOUSE')) && (
            <>
              <Button
                btnSize="small"
                btnStyle={actionActive ? 'success' : ''}
                btnType="iconBefore"
                disabled={event.orderInfo.targetERP === 'INDEX'}
                elevation="2-4"
                handler={() => toggleAction(childEvent)}
              >
                <Icon
                  icon="AddAction"
                  fill={setActionFill((false), actionActive)}
                />
                Resend
              </Button>
              <Button
                btnSize="small"
                btnType="icon"
                disabled={false}
                elevation="2-4"
                handler={() => setState({ modalActive: true })}
              >
                <Icon
                  icon="Close"
                  fill="black"
                />
              </Button>
            </>
            )}
          </div>
          <div className={styles.details}>
            <ListCell label="Dispachter Id" responsive="1280" value={childEvent.dispatcherSalesOrderId || '-'} />
            <ListCell label="Order Qty" responsive="1280" value={childEvent.orderedQuantity ? `${childEvent.orderedQuantity} ${childEvent.unitOfMeasure}` : '-'} />
            <ListCell label="MIN Code" responsive="1280" value={childEvent.itemId || '-'} />
            <ListCell label="PIN Code" responsive="1280" value={childEvent.packId || '-'} />
            <ListCell label="ERP Item Description" responsive="1280" value={childEvent.itemDescription || '-'} />
            <ListCell label="ERP Item Code" responsive="1280" value={childEvent.itemRef || '-'} />
            <ListCell label="PO Ref" responsive="1280" value={childEvent.purchaseOrderRef || '-'} />
            <ListCell label="Supplier Id" responsive="1280" value={childEvent.supplierId || '-'} />
          </div>
        </div>
        <div className={styles.errorMessage}>
          {childEvent.errors && childEvent.errors.map((item, index) => (
          // eslint-disable-next-line react/jsx-key
            <div className="body2"><span>{item.errorMessage}</span> <strong className="subtitle2">&nbsp;&nbsp;{item.resolutionMessage}</strong></div>
          ))}
        </div>
        <Modal
          active={state.modalActive}
        >
          <ModalHeader heading="Event Close" />
          <ModalContent>
            <TextField
              handler={(e:React.ChangeEvent<HTMLInputElement>)  => setState({ comment: e.target.value })}
              label="Comment"
              value={state.comment}
            />
          </ModalContent>
          <ModalFooter>
            <Button handler={() => setState({ modalActive: false })}>
              Cancel
            </Button>
            <Button
              btnStyle="secondary"
              disabled={!state.comment}
              handler={() => setState({ confirmModalActive: true })}
            >
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          active={state.confirmModalActive}
        >
          <ModalHeader heading="Event Close" />
          <ModalContent>
            <p>Are you sure you want to close?</p>
          </ModalContent>
          <ModalFooter>
            <Button handler={() => setState({ modalActive: false, confirmModalActive: false })}>
              Cancel
            </Button>
            <Button
              btnStyle="secondary"
              handler={() => patchData()}
            >
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      </>
    </div>
  );
}

export default SubEvent;
